<template>
  <section v-if="showPositionSection" class="instrument-position">
    <div class="instrument-position__header">
      <div>
        <SkeletonLoader v-if="isLoadingInitialRequest">
          <div class="instrument-position__header__title--loader" />
        </SkeletonLoader>
        <Heading v-else :level="3">Resumo</Heading>
        <SkeletonLoader v-if="isLoading">
          <div
            class="instrument-position__header__text instrument-position__header__text--loader"
          />
        </SkeletonLoader>
        <BaseText
          v-else
          class="instrument-position__header__text"
          size="xs"
          :color="NebraskaColors.elementSecondary"
          >{{ updatedDate }}</BaseText
        >
      </div>
      <SkeletonLoader v-if="isLoadingInitialRequest">
        <div class="instrument-position__header__filter--loader" />
      </SkeletonLoader>
      <div v-else class="instrument-position__header__filters">
        <FilterOption
          v-for="filter in InstrumentPositionFilters"
          :key="filter.value"
          :label="filter.label"
          :value="filter.value"
          :checked="selectedFilter === filter.value"
          @change="handleFilterSelection"
        />
      </div>
    </div>
    <div v-if="hasError" class="instrument-position__error">
      <HelperViewStatus
        title="Não foi possível carregar a sua posição"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="fetchData"
      />
    </div>
    <template v-else>
      <TodayPosition
        v-if="selectedFilter === InstrumentPositionFilterTypes.Today"
        :is-loading="isLoading"
        :position="position"
      />
      <HistoricalPosition
        v-else-if="selectedFilter === InstrumentPositionFilterTypes.Historical"
        :is-loading="isLoading"
        :position="position"
      />
    </template>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';

import {
  BaseText,
  Heading,
  FilterOption,
  HelperViewStatus,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { TodayPosition } from './components/TodayPosition';
import { HistoricalPosition } from './components/HistoricalPosition';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { InstrumentPositionFilters } from './data';
import { InstrumentPositionFilterTypes } from './types';
import {
  getInstrumentPositionHistorical,
  getInstrumentPosition,
  GetInstrumentPositionHistoricalResponse
} from '@/modules/trade/services/instruments';
import { TradeInstrumentPositionPrice } from '@/modules/trade/types/instrument';
import { isRequestError } from '@warrenbrasil/web-http';
import { StringFormatted, NumberFormatted } from '@/modules/trade/types';
import { moneyViewer as money } from '@/modules/common/helpers/currency';

interface Props {
  ticker: string;
  latestPrice: NumberFormatted;
  latestPriceDate: StringFormatted;
}
const props = defineProps<Props>();

const selectedFilter = ref<InstrumentPositionFilterTypes>(
  InstrumentPositionFilterTypes.Today
);
const position = ref<
  TradeInstrumentPositionPrice | GetInstrumentPositionHistoricalResponse | null
>(null);
const updatedDate = ref<string | null>(null);
const isLoading = ref<boolean>(true);
const hasError = ref<boolean>(false);
const abortSignal = ref<AbortController | null>(null);

// Receives a new price in real time and updates the last update date
watch(
  () => props.latestPriceDate,
  () => {
    if (position.value && isTodayFilterSelected.value && props.latestPriceDate)
      updatedDate.value = props.latestPriceDate.formatted;
  }
);

// Receives a new price in real time and updates the user position with it
watch(
  () => props.latestPrice,
  () => {
    if (isTodayFilterSelected.value && props.latestPrice) {
      updateUserPosition(props.latestPrice);
    }
  }
);

const updateUserPosition = (newPrice: NumberFormatted) => {
  if (!position.value) return;

  position.value.price = newPrice;
  if (position.value.quantity) {
    const newCurrentValue = newPrice.value * position.value.quantity;
    position.value.currentValue = {
      value: newCurrentValue,
      formatted: money(newCurrentValue)
    };
  }
};

const handleFilterSelection = async (value: InstrumentPositionFilterTypes) => {
  if (value === selectedFilter.value) return;
  isLoading.value = true;
  selectedFilter.value = value;
  await fetchData();
};

const fetchData = async () => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
  abortSignal.value = new AbortController();

  try {
    isLoading.value = true;
    hasError.value = false;
    let response = null;

    if (selectedFilter.value === InstrumentPositionFilterTypes.Today) {
      response = await getInstrumentPosition(
        props.ticker,
        abortSignal.value.signal
      );
    } else if (
      selectedFilter.value === InstrumentPositionFilterTypes.Historical
    ) {
      response = await getInstrumentPositionHistorical(
        props.ticker,
        abortSignal.value.signal
      );
    }

    position.value = response;
    updatedDate.value = response?.updatedAt?.formatted || '-';
  } catch (e) {
    if (isRequestError(e) && e.type === 'canceled') return;

    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const isLoadingInitialRequest = computed(() => {
  return isLoading.value && !hasError.value && !position.value;
});

const showPositionSection = computed(() => {
  return isLoading.value || hasError.value || position.value;
});

const isTodayFilterSelected = computed(() => {
  return selectedFilter.value === InstrumentPositionFilterTypes.Today;
});

onMounted(async () => {
  await fetchData();
});
</script>

<style lang="less" scoped>
.instrument-position {
  margin-bottom: @size-spacing-x800;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    &__text {
      &--loader {
        width: 100px;
        height: 18px;
        border-radius: @size-radius-x100;
      }
      margin-bottom: @size-spacing-x400;
    }

    &__title {
      &--loader {
        height: 30px;
        margin-bottom: @size-spacing-x75;
        width: 85px;
        border-radius: @size-radius-x100;
      }
    }

    &__filters {
      margin-bottom: @size-spacing-x400;
    }

    &__filter {
      &--loader {
        height: 32px;
        width: 90px;
        border-radius: @size-radius-max;
      }
    }
  }

  &__error {
    display: flex;
    justify-content: center;
  }
}
</style>
